import { useEffect, useRef, useState } from 'react'
import { COLORS, SEMI_TRANSPARENT, FONT_FAMILY } from './theme'

interface ScoreGaugeProps {
  score: number
  scoreLabel: string
  status: 'approved' | 'marginal' | 'rejected'
}

function hexToRgba(hex: string, alpha: number): string {
  const sanitized = hex.replace('#', '')
  const bigint = parseInt(sanitized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function statusText(status: ScoreGaugeProps['status']): string {
  switch (status) {
    case 'approved':
      return 'APROBADO'
    case 'marginal':
      return 'MARGINAL'
    default:
      return 'RECHAZADO'
  }
}

function statusColor(status: ScoreGaugeProps['status']): string {
  switch (status) {
    case 'approved':
      return COLORS.success
    case 'marginal':
      return COLORS.warning
    default:
      return COLORS.secondary
  }
}

export function ScoreGauge({ score, scoreLabel, status }: ScoreGaugeProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = window.devicePixelRatio || 1
    const width = canvas.clientWidth
    const height = canvas.clientHeight
    canvas.width = width * dpr
    canvas.height = height * dpr
    ctx.scale(dpr, dpr)

    const centerX = width / 2
    const centerY = height * 0.85
    const radius = Math.min(width, height * 1.5) * 0.38
    const startAngle = Math.PI
    const endAngle = 2 * Math.PI

    const zones = [
      { from: 0, to: 0.3, color: SEMI_TRANSPARENT.danger },
      { from: 0.3, to: 0.5, color: SEMI_TRANSPARENT.warning },
      { from: 0.5, to: 0.7, color: SEMI_TRANSPARENT.success },
      { from: 0.7, to: 1, color: SEMI_TRANSPARENT.excellent },
    ]

    ctx.clearRect(0, 0, width, height)

    for (const zone of zones) {
      ctx.beginPath()
      ctx.arc(
        centerX,
        centerY,
        radius,
        startAngle + zone.from * Math.PI,
        startAngle + zone.to * Math.PI
      )
      ctx.lineWidth = radius * 0.35
      ctx.strokeStyle = zone.color
      ctx.stroke()
    }

    // Outer arc line
    ctx.beginPath()
    ctx.arc(centerX, centerY, radius, startAngle, endAngle)
    ctx.lineWidth = 1
    ctx.strokeStyle = COLORS.border
    ctx.stroke()

    // Needle
    const clampedScore = Math.min(100, Math.max(0, score))
    const angle = startAngle + (clampedScore / 100) * Math.PI
    const needleLength = radius * 0.85
    const needleX = centerX + Math.cos(angle) * needleLength
    const needleY = centerY + Math.sin(angle) * needleLength

    ctx.beginPath()
    ctx.moveTo(centerX, centerY)
    ctx.lineTo(needleX, needleY)
    ctx.lineWidth = 3
    ctx.strokeStyle = '#ffffff'
    ctx.lineCap = 'round'
    ctx.stroke()

    // Needle base
    ctx.beginPath()
    ctx.arc(centerX, centerY, 5, 0, 2 * Math.PI)
    ctx.fillStyle = '#ffffff'
    ctx.fill()

    // Ticks
    for (let i = 0; i <= 10; i++) {
      const tickAngle = startAngle + (i / 10) * Math.PI
      const x1 = centerX + Math.cos(tickAngle) * (radius + 5)
      const y1 = centerY + Math.sin(tickAngle) * (radius + 5)
      const x2 = centerX + Math.cos(tickAngle) * (radius + 12)
      const y2 = centerY + Math.sin(tickAngle) * (radius + 12)
      ctx.beginPath()
      ctx.moveTo(x1, y1)
      ctx.lineTo(x2, y2)
      ctx.lineWidth = 1
      ctx.strokeStyle = COLORS.textSecondary
      ctx.stroke()

      if (i % 5 === 0) {
        const tx = centerX + Math.cos(tickAngle) * (radius + 24)
        const ty = centerY + Math.sin(tickAngle) * (radius + 24)
        ctx.font = `10px ${FONT_FAMILY}`
        ctx.fillStyle = COLORS.textSecondary
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(i * 10), tx, ty)
      }
    }
  }, [score])

  return (
    <div className="w-full h-full flex flex-col items-center justify-center">
      <h3 className="font-bold text-[14px] mb-1" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
        SCORE MONTE CARLO
      </h3>
      <p className="text-[10px] mb-2" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
        {scoreLabel}
      </p>
      <div className="relative w-full flex-1 min-h-0 cursor-pointer" onClick={() => setShowModal(true)}>
        <canvas ref={canvasRef} className="w-full h-full" />
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none">
          <div className="flex items-baseline">
            <span className="text-[42px] font-bold leading-none" style={{ color: COLORS.primary, fontFamily: FONT_FAMILY }}>
              {Math.round(score)}
            </span>
            <span className="text-[14px] ml-1" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
              /100
            </span>
          </div>
        </div>
      </div>
      <div
        className="px-3 py-1 text-xs font-bold mt-2"
        style={{
          color: statusColor(status),
          backgroundColor: hexToRgba(statusColor(status), 0.2),
          fontFamily: FONT_FAMILY,
        }}
      >
        {statusText(status)}
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setShowModal(false)}
        >
          <div
            className="p-6 max-w-sm w-full border"
            style={{ backgroundColor: COLORS.panel, borderColor: COLORS.border }}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-bold text-[14px] mb-2" style={{ color: COLORS.text, fontFamily: FONT_FAMILY }}>
              Desglose de Score
            </h4>
            <p className="text-[11px]" style={{ color: COLORS.textSecondary, fontFamily: FONT_FAMILY }}>
              Score basado en <strong style={{ color: COLORS.primary }}>{scoreLabel}</strong>. Un score alto indica
              robustez ante diferentes escenarios de Monte Carlo.
            </p>
            <div className="mt-4 text-center">
              <span className="text-[32px] font-bold" style={{ color: statusColor(status), fontFamily: FONT_FAMILY }}>
                {Math.round(score)}
              </span>
              <span className="text-[12px]" style={{ color: COLORS.textSecondary }}> /100</span>
            </div>
            <div className="mt-4 text-center text-xs font-bold" style={{ color: statusColor(status) }}>
              {statusText(status)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
